import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import socketIOClient from "socket.io-client";

import { ChatProperties, NotificationsProperties } from "~/types";

import { getNotificationsService } from "../services/deadpool";

export interface IChatContextData {
  title: string;
  company_domain: string;
  notifications: NotificationsProperties[];
  user: { username: string; email: string; uuid: string; is_partner: boolean };
  company: string;
  isExternal: boolean;
  primary_color: string;
  createChat: boolean;
  chat: ChatProperties;
  submittedTeam: string | null;

  setKeyData(key: string, value: any): void;
  setSubmittedTeam: any;
}

export const ChatContext = createContext<IChatContextData>(
  {} as IChatContextData,
);

export const ChatProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<IChatContextData>({} as IChatContextData);

  const [submittedTeam, setSubmittedTeam] = useState(null);

  useEffect(() => {
    async function getNotifications() {
      const { data } = await getNotificationsService();

      setData(_data => ({
        ..._data,
        notifications: data,
      }));
    }

    getNotifications();
  }, []);

  useEffect(() => {
    const script = document.querySelector("#user_data");

    let username: any;
    let email: any;
    let uuid: any;
    let primary_color: any;
    let is_partner: any;
    let is_external: any;
    let company_domain: any;
    let _company: any;
    let title: any;

    if (process.env.REACT_APP_ENV !== "development") {
      username = script?.getAttribute("username");
      email = script?.getAttribute("email");
      uuid = script?.getAttribute("uuid");
      primary_color = script?.getAttribute("primary_color");
      is_partner = script?.getAttribute("is_partner") === "true";
      is_external = script?.getAttribute("is_external") === "true";
      _company = script?.getAttribute("company");
      company_domain = script?.getAttribute("company_domain");
      title = script?.getAttribute("title");
    } else {
      username = "dev";
      email = "dev@configr.com";
      uuid = "6658b3e9-e746-4d44-b860-c8cfe2534be1";
      primary_color = "#680EB5";
      is_partner = false;
      is_external = true;
      _company = "5278e21e-e651-4883-8370-c61162f58d61";
      company_domain = "https://beta-dev.cloudez.io";
      title = "Vendas Online (Dev)";
    }
    // INTERNAL
    // is_partner determines if it is company owner
    // if is_partner or code is configr goes to admin
    // else goes to partner
    // EXTERNAL
    // if code is configr goes to n1
    // else goes to partner

    if (_company && primary_color) {
      setData(_data => ({
        ..._data,
        company: _company,
        company_domain,
        primary_color,
      }));

      document.documentElement.style.setProperty(
        "--primary-color",
        primary_color,
      );
    }

    if (username && email && uuid && is_partner !== null)
      setData(_data => ({
        ..._data,
        user: {
          username,
          email,
          uuid,
          is_partner,
        },
      }));

    setData(_data => ({
      ..._data,
      title,
      isExternal: is_external,
      createChat: is_external,
    }));
  }, []);

  useMemo(() => {
    if (data.user) {
      const socket = socketIOClient(
        `${process.env.REACT_APP_DEADPOOL_URL}notification`,
        {
          query: {
            uuid: data.user.uuid,
          },
          auth: {
            token: "oi",
          },
          extraHeaders: {
            Authorization: "jwt oi",
          },
        },
      );

      socket?.on("new message", data => {
        setData(_data => ({
          ..._data,
          notifications: [...(_data.notifications as Array<any>), data],
        }));
      });

      socket.on("connect_error", e => {
        console.log(e);
      });

      return socket;
    }

    return null;
  }, [data.user]);

  const setKeyData = useCallback((key: string, value: any) => {
    setData(_data => ({
      ..._data,
      [key]: value,
    }));
  }, []);

  return (
    <ChatContext.Provider
      value={{
        title: data.title,
        notifications: data.notifications,
        isExternal: data.isExternal,
        company: data.company,
        company_domain: data.company_domain,
        user: data.user,
        createChat: data.createChat,
        chat: data.chat,
        primary_color: data.primary_color,
        setKeyData,
        setSubmittedTeam,
        submittedTeam,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export default {
  ChatProvider,
};
